/** Mirrors the backend invoices/invoice_line_items shape (numeric columns arrive as decimal strings). */

/** invoices.status codes (see api/gfgf-api constants InvoiceStatus). */
export const INVOICE_STATUS = {
  draft: 0,
  pending: 1,
  partial_payment: 2,
  paid: 3,
  cancelled: 4,
  overdue: 5,
  voided: 6,
  fail_hold: 7,
  clearing: 8,
  stripe_retry: 9,
} as const;

export const INVOICE_STATUS_LABEL: Record<number, string> = {
  0: "Draft",
  1: "Pending",
  2: "Partial",
  3: "Paid",
  4: "Cancelled",
  5: "Overdue",
  6: "Voided",
  7: "Payment failed",
  8: "Clearing",
  9: "Retrying deposit",
};

/** Plain-language explanation of each status, shown on hover of the status pills / chips. */
export const INVOICE_STATUS_HELP: Record<number, string> = {
  0: "Not finalized yet — still being edited.",
  1: "Finalized and sent; awaiting the client's payment.",
  2: "Part-paid — a balance is still outstanding.",
  3: "Paid and the cash has landed in GFG's bank.",
  4: "Draft discarded before it was ever finalized.",
  5: "Past its due date and still unpaid.",
  6: "Finalized invoice cancelled with no payment taken.",
  7: "Payment failed — any provisioned credits are on hold.",
  8: "Client paid; cash is held at Stripe, awaiting the bank deposit.",
  9: "The bank deposit bounced; Stripe is retrying the payout.",
};

/** Statuses where the client has already paid (no more collection): paid, plus the Stripe cash-timing
 *  states clearing (cash at Stripe, awaiting bank) and stripe_retry (payout bouncing). Used to hide
 *  Pay / Record-payment so an already-settled invoice isn't paid twice. */
export const PAID_LIKE_STATUSES: number[] = [
  INVOICE_STATUS.paid,
  INVOICE_STATUS.clearing,
  INVOICE_STATUS.stripe_retry,
];

/** Status code → the name the server list endpoints accept in `?status=`. */
export const INVOICE_STATUS_PARAM: Record<number, string> = {
  0: "draft",
  1: "pending",
  2: "partial",
  3: "paid",
  4: "cancelled",
  5: "overdue",
  6: "voided",
  7: "fail_hold",
  8: "clearing",
  9: "stripe_retry",
};

/** invoice_line_items.kind. */
export const LINE_KIND = { collection: 0, applied_credit: 1, discount: 2, general: 3 } as const;

export const EXPECTED_PAYMENT_METHOD = ["ach", "wire", "card", "check"] as const;
export type ExpectedPaymentMethod = (typeof EXPECTED_PAYMENT_METHOD)[number];

export const EXPECTED_PAYMENT_CHANNEL = ["stripe", "direct_to_bank"] as const;
export type ExpectedPaymentChannel = (typeof EXPECTED_PAYMENT_CHANNEL)[number];

export interface InvoiceLineItem {
  id: number;
  invoiceId: number;
  kind: number; // LINE_KIND
  collectionId: string | null;
  description: string | null;
  quantity: string | null;
  unitPrice: string | null;
  amount: string | null;
  creditAmount: string | null;
  ccy: string | null;
}

export interface Invoice {
  id: number;
  invoiceNumber: string | null;
  organizationId: number;
  groupId: number;
  status: number; // INVOICE_STATUS
  source: number;
  description: string | null;
  expectedPaymentMethod: number;
  expectedPaymentChannel: number;
  currency: string;
  totalAmount: string | null;
  paidAmount: string | null;
  netRemaining: string | null;
  creditsToMint: string | null;
  /** Discount magnitude (positive) from the single optional Discount line; reduces total, not credits. */
  discount: string;
  /** True once a credit lot has been minted for this invoice (by payment or a sales pre-provision). */
  isMinted: boolean;
  /** When the credit lot was minted (payment or sales pre-provision); null until isMinted flips. */
  mintedAt: string | null;
  /** True when raised by hand in Stripe and imported by Sync with Stripe; null otherwise. */
  importedFromStripe: boolean | null;
  issueDate: string | null;
  dueDate: string | null;
  expectedReceiptDate: string | null;
  paymentReferenceId: string | null;
  cancellationReason: string | null;
  billingAddressId: number | null;
  contactName: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  createdByName: string | null;
  /** Joined names — present on the cross-org Finance list; absent (undefined) on the group list. */
  organizationName?: string | null;
  groupName?: string | null;
  createdAt: string;
  paidAt: string | null;
}

/** A single invoice with its line items (GET detail). */
export interface InvoiceWithLines extends Invoice {
  lineItems: InvoiceLineItem[];
}

/** One row of the invoice activity log (system_event_logs, log_source = "invoice"). actorName is
 *  null for system events (Stripe webhook / overdue cron). */
export interface InvoiceEvent {
  id: number;
  eventType: string;
  actorType: number; // 0 = user, 1 = system
  actorUserId: number | null;
  actorName: string | null;
  statusDescription: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

/** Body for creating an empty draft. */
export interface CreateInvoicePayload {
  expectedPaymentMethod: ExpectedPaymentMethod;
  expectedPaymentChannel?: ExpectedPaymentChannel;
  description?: string | null;
  daysUntilDue?: number;
  paymentReferenceId?: string | null;
  billing?: { contactName?: string | null; email?: string | null };
}

/** payment_receipts.kind — real cash vs a forgiven (non-cash) write-off. */
export const RECEIPT_KIND = { payment: 0, write_off: 1 } as const;

/** payment_receipts.source_type (reuses backend SourceType). */
export const RECEIPT_SOURCE = { payment_method: 0, invoice: 1 } as const;

export const RECEIPT_METHOD_LABEL: Record<number, string> = {
  0: "ACH",
  1: "Wire",
  2: "Card",
  3: "Check",
  4: "Other",
};

/** Payment method options for the record-payment form (value → label). */
export const RECEIPT_METHOD_OPTIONS = [
  { value: "ach", label: "ACH" },
  { value: "wire", label: "Wire" },
  { value: "card", label: "Card" },
  { value: "check", label: "Check" },
  { value: "other", label: "Other" },
] as const;
export const RECEIPT_CHANNEL_LABEL: Record<number, string> = {
  0: "Stripe",
  1: "Bank",
};

/** One row of the payment_receipts ledger (a payment, a write-off, or a reversal of either). A
 *  reversal has reversesReceiptId set and a negative amount. */
export interface PaymentReceipt {
  id: number;
  kind: number; // RECEIPT_KIND
  sourceType: number; // RECEIPT_SOURCE
  sourceId: number;
  organizationId: number;
  groupId: number;
  amount: string; // signed USD
  currency: string;
  method: number | null;
  channel: number | null;
  receivedAt: string;
  reference: string | null;
  note: string | null;
  documentUrl: string | null;
  stripePaymentRecordId: string | null;
  stripeCreditNoteId: string | null;
  reversesReceiptId: number | null;
  actorUserId: number | null;
  actorName: string | null;
  /** The receiving group — shown when the feed spans an organization's groups. */
  groupName?: string | null;
  createdAt: string;
}

/** Query for a page of the purchases feed (a group's, or an organization's across groups). */
export interface ReceiptListQuery {
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  limit?: number;
}

/** A recorded refund (client's out-of-band money-back, bookkeeping-only) for the Finance Refunds
 *  blotter, enriched with the invoice number and org/group/actor names. */
export interface Refund {
  id: number;
  invoiceId: number;
  paymentReceiptId: number;
  organizationId: number;
  groupId: number;
  amount: string; // SIGNED USD: refund +, reversal −
  currency: string;
  refundDate: string;
  reason: string;
  reference: string | null;
  /** True on an original refund once reversed. */
  isReversed: boolean;
  /** Set on a reversal row → the refund it reverses; null = an original refund. */
  reversesRefundId: number | null;
  actorUserId: number | null;
  createdAt: string;
  invoiceNumber: string | null;
  organizationName: string | null;
  groupName: string | null;
  actorName: string | null;
}

/** A payment on an invoice with its remaining refundable balance (New Refund picker). */
export interface RefundablePayment {
  paymentReceiptId: number;
  amount: string; // the original payment
  refunded: string; // net already refunded
  remaining: string; // amount − refunded
  currency: string;
  method: number | null;
  receivedAt: string;
}

/** Body for recording a refund against a specific payment on an invoice. */
export interface CreateRefundPayload {
  invoiceId: number;
  paymentReceiptId: number;
  amount: number;
  refundDate: string; // ISO date/datetime — the client's actual refund date
  reason: string;
  reference?: string;
}

/** Body for reversing a recorded refund. */
export interface ReverseRefundPayload {
  reason: string;
  reversalDate: string; // ISO
}

/** The credit-lot decision offered at the reverse-payment prompt. */
export type ReverseLotAction = "none" | "hold" | "reverse";

/** Body for recording a payment (full/partial) or write-off against an invoice. */
export interface RecordPaymentPayload {
  amount?: number; // omit → full outstanding balance
  method?: "ach" | "wire" | "card" | "check" | "other";
  channel?: "stripe" | "direct_to_bank";
  receivedAt?: string; // ISO datetime
  reference?: string | null;
  note?: string | null;
  documentUrl?: string | null;
  writeOffRemainder?: boolean;
}

/** A line to add to a draft (discriminated by kind). */
export type AddLinePayload =
  | {
      kind: "collection";
      collectionId: string;
      description: string;
      unitPrice: number;
      quantity: number;
    }
  // General: the biller types the title; otherwise identical to a collection line (unit × qty).
  | { kind: "general"; description: string; unitPrice: number; quantity: number }
  | { kind: "applied_credit"; description?: string; amount: number }
  // Discount: a positive magnitude entered by the user (stored server-side as a negative line).
  | { kind: "discount"; description?: string; amount: number };
